import React, { useEffect, useRef, useState } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';

interface VideoJSPlayerProps {
  videoUrl: string;
  videoType?: string;         // 🚀 NEW: MIME type từ file gốc
  srtUrl?: string;
  vttUrl?: string;            // 🚀 NEW: VTT subtitle URL
  translatedSrtUrl?: string;
  translatedVttUrl?: string;  // 🚀 NEW: Translated VTT subtitle URL
  originalSrtUrl?: string;
  fallbackVideoUrl?: string;
  className?: string;
  onReady?: () => void;
  onError?: (error: any) => void;
  onSubtitleUpdate?: (newSrtUrl: string) => void;
}

export default function VideoJSPlayer({
  videoUrl,
  videoType,                  // 🚀 NEW
  srtUrl,
  vttUrl,                     // 🚀 NEW
  translatedSrtUrl,
  translatedVttUrl,           // 🚀 NEW
  originalSrtUrl,
  fallbackVideoUrl,
  className = "w-full h-[18rem] md:h-[32rem]",
  onReady,
  onError,
  onSubtitleUpdate
}: VideoJSPlayerProps) {
  console.log('🎬 VideoJSPlayer - Component initialized with props:', {
    videoUrl,
    videoType,
    srtUrl,
    vttUrl,                     // 🚀 NEW
    translatedSrtUrl,
    translatedVttUrl,           // 🚀 NEW
    originalSrtUrl,
    fallbackVideoUrl,
    className
  });

  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  
  // 🚀 NEW: Mobile Safari detection
  const isMobileSafari = () => {
    const ua = navigator.userAgent;
    return /iPad|iPhone|iPod/.test(ua) && /Safari/.test(ua) && !/Chrome|CriOS|FxiOS/.test(ua);
  };

  // 🚀 NEW: Reset error state when videoUrl changes
  useEffect(() => {
    setHasError(false);
    setIsLoading(true);
  }, [videoUrl]);

  // 🚀 Build subtitle tracks for Video.js - CHỈ DÙNG VTT (SRT chỉ để download)
  // 🚀 Helper: Extract language code from URL
  const langFrom = (url?: string, fallback = 'en') =>
    (url?.match(/[-_\.]([a-z]{2})(?:[-_\.]|$)/i)?.[1] || fallback).toLowerCase();

  const buildSubtitleTracks = () => {
    const tracks: any[] = [];
    
    // 🚀 CHỈ DÙNG VTT: Add original VTT subtitle
    if (vttUrl) {
      tracks.push({
        kind: 'subtitles',
        src: vttUrl,
        srclang: langFrom(vttUrl, 'en'),
        label: 'Original',
        default: !translatedVttUrl
      });
    }
    
    // 🚀 CHỈ DÙNG VTT: Add translated VTT subtitle
    if (translatedVttUrl) {
      tracks.push({
        kind: 'subtitles',
        src: translatedVttUrl,
        srclang: langFrom(translatedVttUrl, 'en'),
        label: 'Translated',
        default: true
      });
    }
    
    // ❌ BỎ SRT: Video.js không đọc được SRT, chỉ dùng để download
    // SRT URLs (srtUrl, originalSrtUrl, translatedSrtUrl) chỉ dùng cho download buttons
    
    return tracks;
  };

  // 🚀 FIXED: Determine effective video URL - bỏ check blob sai
  const getEffectiveVideoUrl = () => {
    if (!videoUrl || videoUrl.trim() === '') {
      console.warn('⚠️ VideoJSPlayer - No video URL provided, using fallback');
      return fallbackVideoUrl || '';
    }
    
    // 🚀 FLEXIBLE: Use fallback if video URL is invalid
    if (videoUrl === "SKIP_MP4_LOCAL" || videoUrl === "INVALID") {
      console.log('🚀 VideoJSPlayer - Using fallback video URL');
      return fallbackVideoUrl || '';
    }
    
    // 🚀 NEW: Check if blob URL is still valid
    if (videoUrl.startsWith('blob:')) {
      try {
        // Test if blob URL is still accessible
        const url = new URL(videoUrl);
        if (url.protocol === 'blob:') {
          return videoUrl;
        }
      } catch (e) {
        console.warn('⚠️ VideoJSPlayer - Invalid blob URL, using fallback');
        return fallbackVideoUrl || '';
      }
    }
    
    // ✅ Mọi URL khác: đều hợp lệ
    return videoUrl;
  };

  // 🚀 FIXED: Detect video type - ưu tiên URL, fallback S3 luôn MP4
  const getVideoType = (url: string) => {
    // Nếu là fallback S3 -> chắc chắn mp4
    if (fallbackVideoUrl && url === fallbackVideoUrl) return 'video/mp4';

    // Ưu tiên đoán theo đuôi URL khi KHÔNG phải blob:
    if (!url.startsWith('blob:')) {
      if (url.includes('.mp4')) return 'video/mp4';
      if (url.includes('.webm')) return 'video/webm';
      if (url.includes('.ogg')) return 'video/ogg';
      if (url.includes('.mov')) return 'video/quicktime';
      if (url.includes('.mkv')) return 'video/x-matroska';
      if (url.includes('.avi')) return 'video/x-msvideo';
    }

    // Chỉ dùng videoType props như fallback cuối cùng (blob…)
    if (videoType) return videoType;

    // Mặc định an toàn
    return 'video/mp4';
  };

  // 🚀 Initialize Video.js player
  useEffect(() => {
    if (!videoRef.current) return;

    const effectiveUrl = getEffectiveVideoUrl();
    
    if (!effectiveUrl) {
      console.error('❌ VideoJSPlayer - No video URL available');
      setHasError(true);
      setIsLoading(false);
      return;
    }

    console.log('🎬 VideoJSPlayer - Initializing with:', {
      originalUrl: videoUrl,
      effectiveUrl,
      srtUrl,
      translatedSrtUrl,
      originalSrtUrl,
      fallbackVideoUrl,
      isObjectUrl: effectiveUrl.startsWith('blob:')
    });

    // Clean up existing player safely
    if (playerRef.current) {
      try {
        // Clear any pending timeouts or intervals
        
        // Dispose player safely
        playerRef.current.dispose();
        playerRef.current = null;
      } catch (error) {
        console.warn('⚠️ VideoJSPlayer - Error during player cleanup:', error);
        playerRef.current = null;
      }
    }

    // 🚀 MULTI-SOURCE: Tạo mảng sources để Video.js tự chọn nguồn hỗ trợ
    const sources = [
      {
        src: effectiveUrl,
        type: getVideoType(effectiveUrl)
      },
      ...(fallbackVideoUrl && fallbackVideoUrl !== effectiveUrl ? [{
        src: fallbackVideoUrl,
        type: 'video/mp4'
      }] : [])
    ];

    console.log('🔧 VideoJSPlayer - Initializing player with multi-source:', {
      videoElement: videoRef.current,
      sources,
      effectiveUrl,
      fallbackVideoUrl
    });
    
    const player = videojs(videoRef.current, {
      controls: true,
      responsive: true,
      fluid: false, // 🚀 FIX: Disable fluid để tránh padding-top hack
      fill: true,   // 🚀 FIX: Fill container instead of fluid
      preload: 'metadata', // 🚀 Preload metadata only (duration, dimensions)
      sources,
      // 🚀 NEW: Optimized preload settings for faster playback
      techOrder: ['html5'], // Use HTML5 first
      html5: {
        vhs: {
          overrideNative: true,
          enableLowInitialPlaylist: true, // Start with low quality
          smoothQualityChange: true, // Smooth quality transitions
          allowSeeksWithinUnsafeLiveWindow: true,
          // 🚀 NEW: Buffer settings for faster start
          maxBufferLength: 30, // Max 30 seconds buffer
          maxBufferSize: 60 * 1000 * 1000, // 60MB max buffer
          maxBufferHole: 0.1, // Max 100ms hole in buffer
          // 🚀 NEW: Preload settings
          preload: 'metadata', // Only preload metadata
          preloadTime: 0, // Don't preload any video data
          // 🚀 NEW: Quality settings
          limitRenditionByPlayerDimensions: true,
          // 🚀 NEW: Bandwidth settings
          bandwidth: 4194304, // 4Mbps default
          useDevicePixelRatio: true
        },
        // 🚀 FIX: Mobile Safari subtitle compatibility
        nativeVideoTracks: false,
        nativeAudioTracks: false,
        nativeTextTracks: false, // Force Video.js to handle text tracks
        // 🚀 NEW: Mobile Safari specific settings
        enableSmoothSeeking: true,
        enableLowInitialPlaylist: true,
        // 🚀 NEW: CORS and subtitle loading settings
        crossorigin: 'anonymous',
        // 🚀 NEW: Mobile Safari subtitle workarounds
        mobile: {
          nativeTextTracks: false, // Force Video.js text track handling
          nativeControlsForTouch: false, // Use Video.js controls on mobile
          // 🚀 NEW: Enable touch events for mobile
          touch: true, // Enable touch events
          clickToPlay: true // Enable click to play on mobile
        }
      },
      // 🚀 NEW: Buffer settings for faster start
      playbackRates: [0.5, 1, 1.25, 1.5, 2], // Playback speed options
      liveui: false, // Disable live UI for VOD content
      loadingSpinner: true, // Show loading spinner
      bigPlayButton: true, // Show big play button
      poster: '', // No poster image
      // 🚀 NEW: Auto-play settings
      autoplay: false, // Don't auto-play (user gesture required)
      muted: true, // Muted by default (required for autoplay)
      playsinline: true, // Play inline on mobile
      // 🚀 NEW: Additional preload optimizations
      inactivityTimeout: 2000, // Hide controls after 2s
      userActions: {
        hotkeys: true, // Enable keyboard shortcuts
        doubleClick: true, // Double-click to toggle fullscreen
        click: true // Enable click to play/pause
      },
      // 🚀 NEW: Buffer optimization
      buffer: {
        maxBufferLength: 30, // Max 30 seconds buffer
        maxBufferSize: 60 * 1000 * 1000, // 60MB max buffer
        maxBufferHole: 0.1 // Max 100ms hole in buffer
      }
    });

    // Event handlers
    player.ready(() => {
      console.log('✅ VideoJSPlayer - Player ready');
      setIsLoading(false);
      setHasError(false);
      
      // ✅ Không cần tự add class bằng JS nữa - CSS global sẽ handle
      
      // 🚀 FIX: Ensure tech fills container with proper object-fit
      const tech = player.el().querySelector('.vjs-tech') as HTMLVideoElement | null;
      if (tech) {
        tech.style.objectFit = 'contain';
      }
      player.width('100%');
      player.height('100%'); // Fill wrapper 24rem
      
      // 🚀 FIX: Mobile Safari subtitle initialization
      if (isMobileSafari()) {
        console.log('🍎 VideoJSPlayer - Mobile Safari detected, applying subtitle fixes');
        
        // Force Video.js to handle text tracks on mobile Safari
        const videoElement = player.el().querySelector('video');
        if (videoElement) {
          // Ensure video element has proper attributes for mobile Safari
          videoElement.setAttribute('crossorigin', 'anonymous');
          videoElement.setAttribute('playsinline', 'true');
          
          // Add mobile Safari specific event listeners
          videoElement.addEventListener('loadedmetadata', () => {
            console.log('🍎 VideoJSPlayer - Mobile Safari metadata loaded, checking subtitles');
            // Force subtitle track refresh after metadata loads
            setTimeout(() => {
              const tracks = player.textTracks();
              if (tracks) {
                console.log('🍎 VideoJSPlayer - Mobile Safari found text tracks');
                // Force activation of the first available track
                // Use for...in loop to iterate over TextTrackList
                for (let i = 0; i < (tracks as any).length; i++) {
                  const track = (tracks as any)[i] as TextTrack;
                  if (track && track.kind === 'subtitles') {
                    track.mode = 'showing';
                    console.log('🍎 VideoJSPlayer - Mobile Safari activated subtitle track:', track.label);
                    break;
                  }
                }
              }
            }, 200);
          });
        }
      }
      
      // 🚀 FIX: Add event listener to hide play button when video starts
      const videoElement = player.el().querySelector('video');
      if (videoElement) {
        const hidePlayButton = () => {
          const playButton = player.el().querySelector('.vjs-big-play-button');
          if (playButton) {
            (playButton as HTMLElement).style.display = 'none';
          }
        };
        
        const showPlayButton = () => {
          console.log('🔄 Native event - Showing play button');
          const playButton = player.el().querySelector('.vjs-big-play-button');
          if (playButton) {
            console.log('🔍 Native event - Found play button, showing it');
            (playButton as HTMLElement).style.display = 'flex';
            (playButton as HTMLElement).style.visibility = 'visible';
            (playButton as HTMLElement).style.opacity = '1';
            // Force remove any classes that might hide it
            (playButton as HTMLElement).classList.remove('vjs-hidden');
          } else {
            console.log('❌ Native event - Play button not found');
          }
        };
        
        videoElement.addEventListener('play', hidePlayButton);
        videoElement.addEventListener('pause', showPlayButton);
        videoElement.addEventListener('ended', showPlayButton);
        
        // 🚀 NEW: Add touch event handler for mobile
        const handleTouchEnd = (event: TouchEvent) => {
          // Only handle single touch (not multi-touch gestures)
          if (event.touches.length === 0 && event.changedTouches.length === 1) {
            console.log('📱 VideoJSPlayer - Mobile touch detected, toggling play/pause');
            
            if (player) {
              if (player.paused()) {
                player.play()?.catch((error) => {
                  console.error('❌ VideoJSPlayer - Mobile play failed:', error);
                });
              } else {
                player.pause();
              }
            }
          }
        };
        
        videoElement.addEventListener('touchend', handleTouchEnd);
      }
      
      
      onReady?.();
    });


    // 🚀 NEW: Preload optimization events
    player.on('loadstart', () => {
      console.log('🔄 VideoJSPlayer - Load started');
      setIsLoading(true);
    });

    // 🚀 NEW: Detect network errors early
    player.on('error', (error: any) => {
      const errorCode = player.error()?.code;
      console.error('❌ VideoJSPlayer - Error detected:', {
        errorCode,
        error,
        currentSrc: player.currentSrc(),
        networkState: player.networkState(),
        readyState: player.readyState()
      });
      
    // 🚀 MULTI-SOURCE: Video.js sẽ tự chọn nguồn hỗ trợ, không cần fallback thủ công
    console.log('⚠️ VideoJSPlayer - Video source failed, Video.js will try next source automatically');
    setHasError(true);
    setIsLoading(false);
    onError?.(error);
    });

    // 🚀 MULTI-SOURCE: Video.js sẽ tự xử lý error và chuyển sang source tiếp theo
    player.on('error', (error: any) => {
      const errorCode = player.error()?.code;
      console.warn(`⚠️ VideoJSPlayer - Error ${errorCode} detected, Video.js will try next source automatically`);
    });

    // 🚀 MULTI-SOURCE: Video.js sẽ tự xử lý timeout/decode errors

    // 🚀 MULTI-SOURCE: Video.js sẽ tự xử lý loading timeout
    player.on('loadstart', () => {
      console.log('🔄 VideoJSPlayer - Load started');
      setIsLoading(true);
    });

    player.on('loadedmetadata', () => {
      console.log('📊 VideoJSPlayer - Metadata loaded (duration, dimensions)');
      // Video is ready to show duration and dimensions
      setIsLoading(false); // 🚀 NEW: Stop loading when metadata is ready
      
      // Clear loading timeout
    });

    player.on('loadeddata', () => {
      console.log('📊 VideoJSPlayer - First frame loaded');
      // First frame is ready, video can start playing
      setIsLoading(false); // 🚀 NEW: Stop loading when first frame is ready
      
      // Clear loading timeout
    });

    // 🚀 MULTI-SOURCE: Video.js sẽ tự xử lý network state changes

    player.on('canplay', () => {
      console.log('✅ VideoJSPlayer - Can play (enough data buffered)');
      setIsLoading(false);
      
      // Clear loading timeout
    });

    // 🚀 MULTI-SOURCE: Video.js sẽ tự xử lý ready state changes

    player.on('canplaythrough', () => {
      console.log('✅ VideoJSPlayer - Can play through (entire video buffered)');
      // Entire video is buffered and ready to play
    });

    // 🚀 FIX: Hide play button when video starts playing
    player.on('play', () => {
      console.log('▶️ VideoJSPlayer - Video started playing, hiding play button');
      const playButton = player.el().querySelector('.vjs-big-play-button');
      if (playButton) {
        (playButton as HTMLElement).style.display = 'none';
      }
    });

    // 🚀 FIX: Show play button when video is paused
    player.on('pause', () => {
      console.log('⏸️ VideoJSPlayer - Video paused, showing play button');
      
      // Use setTimeout to ensure Video.js has finished processing
      setTimeout(() => {
        const playButton = player.el().querySelector('.vjs-big-play-button');
        if (playButton) {
          console.log('🔍 Found play button, showing it');
          (playButton as HTMLElement).style.display = 'flex';
          (playButton as HTMLElement).style.visibility = 'visible';
          (playButton as HTMLElement).style.opacity = '1';
          (playButton as HTMLElement).classList.remove('vjs-hidden');
        } else {
          console.log('❌ Play button not found when trying to show');
        }
      }, 100); // Small delay to let Video.js process
    });

    // 🚀 FIX: Show play button when user becomes inactive (mouse leaves)
    player.on('userinactive', () => {
      if (player.paused()) {
        console.log('👤 VideoJSPlayer - User inactive and video paused, showing play button');
        const playButton = player.el().querySelector('.vjs-big-play-button');
        if (playButton) {
          (playButton as HTMLElement).style.display = 'flex';
          (playButton as HTMLElement).style.visibility = 'visible';
          (playButton as HTMLElement).style.opacity = '1';
        }
      }
    });

    // 🚀 NEW: Progress events for better UX
    player.on('progress', () => {
      const buffered = player.buffered();
      if (buffered.length > 0) {
        const bufferedEnd = buffered.end(buffered.length - 1);
        const duration = player.duration();
        if (duration && duration > 0) {
          const percent = (bufferedEnd / duration) * 100;
          console.log(`📊 VideoJSPlayer - Buffered: ${percent.toFixed(1)}%`);
        }
      }
    });

    playerRef.current = player;

    // Cleanup on unmount
    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [videoUrl, fallbackVideoUrl, videoType]);

  // 🚀 Update subtitles when props change
  useEffect(() => {
    if (!playerRef.current) return;

    const tracks = buildSubtitleTracks();
    console.log('🔄 VideoJSPlayer - Updating subtitle tracks:', tracks);
    
    // Update tracks
    playerRef.current.ready(() => {
      // 1) Remove existing REMOTE tracks (đúng API)
      const remote = playerRef.current.remoteTextTracks();
      for (let i = remote.length - 1; i >= 0; i--) {
        playerRef.current.removeRemoteTextTrack(remote[i]);
      }

      // 2) Add new tracks with mobile Safari compatibility
      tracks.forEach(track => {
        // 🚀 FIX: Mobile Safari subtitle compatibility
        const enhancedTrack = {
          ...track,
          // Ensure proper CORS headers for mobile Safari
          crossorigin: 'anonymous',
          // Add mobile Safari specific attributes
          preload: 'metadata',
          // Force Video.js to handle the track
          mode: 'disabled' // Will be enabled below
        };
        
        console.log('🔄 VideoJSPlayer - Adding track:', enhancedTrack);
        playerRef.current.addRemoteTextTrack(enhancedTrack, false);
      });

      // 3) 🚀 FIX: Mobile Safari subtitle activation with delay
      const activateSubtitles = () => {
        const tt = playerRef.current.textTracks();
        console.log('🔄 VideoJSPlayer - Available text tracks:', tt.length);
        
        // Disable all tracks first
        for (let i = 0; i < tt.length; i++) {
          tt[i].mode = 'disabled';
        }
        
        // ưu tiên translated nếu có
        let active: TextTrack | null = null;
        for (let i = 0; i < tt.length; i++) {
          if ((tt[i] as any).label === 'Translated') { 
            active = tt[i]; 
            break; 
          }
        }
        if (!active && tt.length) active = tt[0];
        
        if (active) {
          console.log('🔄 VideoJSPlayer - Activating track:', active.label);
          active.mode = 'showing';
          
          // 🚀 FIX: Mobile Safari subtitle display workaround
          // Force subtitle display by triggering a track change event
          if (active.cues && active.cues.length > 0) {
            console.log('🔄 VideoJSPlayer - Track has cues, forcing display');
            // Trigger a custom event to ensure subtitles are displayed
            const event = new Event('change');
            active.dispatchEvent(event);
          }
          
          // 🚀 FIX: Additional mobile Safari workaround
          if (isMobileSafari()) {
            console.log('🍎 VideoJSPlayer - Mobile Safari subtitle workaround applied');
            // Force a refresh of the subtitle display
            setTimeout(() => {
              if (active && active.cues && active.cues.length > 0) {
                // Trigger cue change events to force display
                for (let i = 0; i < active.cues.length; i++) {
                  const cue = active.cues[i];
                  if (cue) {
                    const cueEvent = new Event('cuechange');
                    cue.dispatchEvent(cueEvent);
                  }
                }
              }
            }, 50);
          }
        }
      };
      
      // Apply delay for mobile Safari
      setTimeout(activateSubtitles, isMobileSafari() ? 300 : 100);
    });
  }, [srtUrl, vttUrl, translatedSrtUrl, translatedVttUrl, originalSrtUrl]); // 🚀 FIX: Track all subtitle props for proper reload

  return (
    <div className={`jwlike ${className} relative overflow-hidden`}>
      {/* 🎨 CUSTOM: JWPlayer-style play button with global CSS */}
      <style>{`
        /* Ẩn icon font mặc định */
        .jwlike .vjs-big-play-button .vjs-icon-placeholder:before {
          content: none !important;
        }

        /* Nút tròn căn giữa hoàn hảo vào video */
        .jwlike .vjs-big-play-button {
          width: 118px !important; /* 168px - 30% = 118px */
          height: 118px !important; /* 168px - 30% = 118px */
          border: 0 !important;
          border-radius: 50% !important;
          background: linear-gradient(135deg, rgba(0,0,0,.75), rgba(0,0,0,.55)) !important;
          box-shadow: 0 8px 30px rgba(0,0,0,.35), 0 0 0 0 rgba(255,255,255,.18) !important;
          transition: transform .25s cubic-bezier(.4,0,.2,1), box-shadow .25s, background .25s !important;
          cursor: pointer !important;
          /* 🎯 BEST: Căn giữa hoàn hảo với flexbox */
          position: absolute !important;
          top: 50% !important;
          left: 50% !important;
          transform: translate(-50%, -50%) !important; /* Center both X and Y */
          margin: 0 !important; /* Reset any margin */
        }

        /* Tam giác play kiểu JW - GIẢM 30% */
        .jwlike .vjs-big-play-button::after {
          content: "" !important;
          position: absolute !important;
          left: 50% !important;
          top: 50% !important;
          transform: translate(-36%, -50%) !important; /* đẩy nhẹ sang phải cho đúng thị giác */
          width: 0 !important;
          height: 0 !important;
          border-left: 34px solid #fff !important; /* 48px - 30% = 34px */
          border-top: 21px solid transparent !important; /* 30px - 30% = 21px */
          border-bottom: 21px solid transparent !important; /* 30px - 30% = 21px */
          filter: drop-shadow(0 2px 4px rgba(0,0,0,.35)) !important;
        }

        /* Hover/focus */
        .jwlike .vjs-big-play-button:hover {
          transform: translate(-50%, -50%) scale(1.05) !important; /* Giữ center + scale */
          background: linear-gradient(135deg, rgba(0,0,0,.9), rgba(0,0,0,.7)) !important;
          box-shadow: 0 12px 36px rgba(0,0,0,.45), 0 0 0 12px rgba(255,255,255,.06) !important;
        }
        .jwlike .vjs-big-play-button:active {
          transform: translate(-50%, -50%) scale(.96) !important; /* Giữ center + scale */
        }
        .jwlike .vjs-big-play-button:focus {
          outline: none !important;
        }

        /* Ripple/pulse nhẹ kiểu JW */
        .jwlike .vjs-big-play-button {
          animation: jwPulse 3s infinite !important;
        }
        @keyframes jwPulse {
          0%   { box-shadow: 0 8px 30px rgba(0,0,0,.35), 0 0 0 0 rgba(255,255,255,.12); }
          50%  { box-shadow: 0 12px 40px rgba(0,0,0,.45), 0 0 0 16px rgba(255,255,255,.05); }
          100% { box-shadow: 0 8px 30px rgba(0,0,0,.35), 0 0 0 0 rgba(255,255,255,.12); }
        }

        /* Ẩn nút khi đã start (Video.js sẽ add class này) */
        .jwlike.video-js.vjs-has-started .vjs-big-play-button,
        .jwlike .video-js.vjs-has-started .vjs-big-play-button,
        .video-js.vjs-has-started .vjs-big-play-button {
          display: none !important;
        }

        /* 🚀 FIX: Additional selectors to ensure button is hidden when playing */
        .jwlike .video-js.vjs-playing .vjs-big-play-button,
        .jwlike .video-js.vjs-user-inactive .vjs-big-play-button,
        .video-js.vjs-playing .vjs-big-play-button {
          display: none !important;
        }

        /* 🚀 FIX: Force hide when video is not paused */
        .jwlike .video-js:not(.vjs-paused) .vjs-big-play-button {
          display: none !important;
        }

        /* 🚀 FIX: Ensure button is visible when paused */
        .jwlike .video-js.vjs-paused .vjs-big-play-button {
          display: flex !important;
          visibility: visible !important;
          opacity: 1 !important;
        }

        /* 🎯 BONUS: Đảm bảo container video có position relative */
        .jwlike .video-js {
          position: relative !important;
        }

        /* 🎯 BONUS: Alternative method - sử dụng flexbox cho container */
        .jwlike .vjs-big-play-button {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }

        /* Mobile size nhỏ hơn - GIẢM 30% */
        @media (max-width: 768px) {
          .jwlike .vjs-big-play-button { 
            width: 90px !important; /* 128px - 30% = 90px */
            height: 90px !important; /* 128px - 30% = 90px */
          }
          .jwlike .vjs-big-play-button::after {
            border-left-width: 28px !important; /* 40px - 30% = 28px */
            border-top-width: 17px !important; /* 24px - 30% = 17px */
            border-bottom-width: 17px !important; /* 24px - 30% = 17px */
          }
        }

        /* 🚀 FIX: Desktop subtitle display - Normal size */
        .jwlike .vjs-text-track-display {
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          pointer-events: none !important;
          z-index: 15 !important;
          display: flex !important;
          align-items: flex-end !important; /* 🚀 FIX: Align to bottom for subtitle positioning */
          justify-content: center !important;
          padding: 0 5% 50px 5% !important; /* 🚀 FIX: Add bottom padding to position subtitle at bottom */
        }

        /* 🚀 FIX: Force subtitle positioning to bottom - Override Video.js defaults */
        .jwlike .vjs-text-track-display .vjs-text-track-cue {
          position: absolute !important;
          bottom: 50px !important; /* 🚀 FIX: Force position at bottom */
          top: auto !important; /* 🚀 FIX: Remove top positioning */
          left: 0% !important; /* 🚀 FIX: Start from left edge (increased from 5%) */
          right: 0% !important; /* 🚀 FIX: End at right edge (increased from 5%) */
          transform: none !important; /* 🚀 FIX: Remove transform */
          width: auto !important; /* 🚀 FIX: Auto width within constraints */
          max-width: none !important; /* 🚀 FIX: Remove max-width constraint */
        }

        .jwlike .vjs-text-track-cue {
          position: relative !important; /* 🚀 FIX: Use relative positioning within container */
          top: 0 !important; /* 🚀 FIX: Reset top positioning */
          bottom: auto !important; /* 🚀 FIX: Remove bottom positioning */
          left: 0 !important; /* 🚀 FIX: Start from left edge */
          right: auto !important; /* 🚀 FIX: Remove right positioning */
          transform: none !important; /* 🚀 FIX: Remove transform for full width */
          text-align: center !important;
          background: transparent !important; /* 🚀 FIX: No background - clean subtitle text only */
          color: white !important;
          font-size: 2.1em !important; /* 🚀 FIX: 50% larger font for desktop (1.4em * 1.5) */
          line-height: 1.4 !important;
          padding: 1.2em !important; /* 🚀 FIX: 50% more padding for desktop (0.8em * 1.5) */
          border-radius: 0.3em !important;
          margin: 0 !important; /* 🚀 FIX: Remove margin to prevent shifting */
          max-width: none !important; /* 🚀 FIX: Remove max-width constraint */
          width: 100% !important; /* 🚀 FIX: Full width */
          word-wrap: break-word !important; /* 🚀 FIX: Break long words */
          word-break: break-word !important; /* 🚀 FIX: Force break long words */
          white-space: normal !important; /* 🚀 FIX: Allow line breaks */
          overflow-wrap: break-word !important; /* 🚀 FIX: Modern word wrapping */
          hyphens: auto !important; /* 🚀 FIX: Add hyphens for better breaking */
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
          box-sizing: border-box !important; /* 🚀 FIX: Include padding in width calculation */
        }

        /* 🚀 FIX: Mobile-specific subtitle styling - 50% larger */
        @media (max-width: 768px) {
          .jwlike .vjs-text-track-display .vjs-text-track-cue {
            position: absolute !important;
            bottom: 63px !important; /* 🚀 FIX: Move up 3px more from 60px to 63px on mobile */
            top: auto !important; /* 🚀 FIX: Remove top positioning */
            left: 50% !important; /* 🚀 FIX: Center horizontally */
            right: auto !important; /* 🚀 FIX: Remove right positioning */
            transform: translateX(-50%) !important; /* 🚀 FIX: Perfect center alignment */
            width: 90% !important; /* 🚀 FIX: Increase width by 20% on mobile (70% + 20% = 90%) */
            max-width: 90% !important; /* 🚀 FIX: Set max-width to 90% */
            font-size: 1.8em !important; /* 50% larger on mobile (1.2em * 1.5) */
            padding: 0.75em !important; /* 50% more padding on mobile (0.5em * 1.5) */
            margin: 0 !important; /* 🚀 FIX: Remove margin for perfect centering */
            word-wrap: break-word !important; /* 🚀 FIX: Break long words */
            word-break: break-word !important; /* 🚀 FIX: Force break long words */
            overflow-wrap: break-word !important; /* 🚀 FIX: Modern word wrapping */
            hyphens: auto !important; /* 🚀 FIX: Add hyphens for better breaking */
          }
        }

        /* 🚀 FIX: Mobile Safari specific subtitle styling - 50% larger */
        @media screen and (-webkit-min-device-pixel-ratio: 0) and (max-width: 768px) {
          .jwlike .vjs-text-track-display .vjs-text-track-cue {
            position: absolute !important;
            bottom: 63px !important; /* 🚀 FIX: Move up 3px more from 60px to 63px on mobile Safari */
            top: auto !important; /* 🚀 FIX: Remove top positioning */
            left: 50% !important; /* 🚀 FIX: Center horizontally */
            right: auto !important; /* 🚀 FIX: Remove right positioning */
            -webkit-transform: translateX(-50%) translateZ(0) !important; /* 🚀 FIX: Center + hardware acceleration */
            transform: translateX(-50%) translateZ(0) !important; /* 🚀 FIX: Center + hardware acceleration */
            will-change: transform !important;
            width: 90% !important; /* 🚀 FIX: Increase width by 20% on mobile Safari (70% + 20% = 90%) */
            max-width: 90% !important; /* 🚀 FIX: Set max-width to 90% */
            font-size: 1.8em !important; /* 50% larger (1.2em * 1.5) */
            padding: 0.75em !important; /* 50% more padding (0.5em * 1.5) */
            margin: 0 !important; /* 🚀 FIX: Remove margin for perfect centering */
            word-wrap: break-word !important; /* 🚀 FIX: Break long words */
            word-break: break-word !important; /* 🚀 FIX: Force break long words */
            overflow-wrap: break-word !important; /* 🚀 FIX: Modern word wrapping */
            hyphens: auto !important; /* 🚀 FIX: Add hyphens for better breaking */
          }
        }

        /* 🚀 FIX: Ensure subtitles are visible on all devices */
        .jwlike .vjs-text-track-display .vjs-text-track-cue {
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
        }

        /* 🚀 NEW: Make video clickable for play/pause toggle */
        .jwlike .vjs-tech {
          cursor: pointer !important;
          user-select: none !important;
          -webkit-touch-callout: none !important; /* Disable callout on iOS */
          -webkit-user-select: none !important; /* Disable text selection on iOS */
          touch-action: manipulation !important; /* Enable touch events */
        }

        .jwlike video {
          cursor: pointer !important;
          user-select: none !important;
          -webkit-touch-callout: none !important; /* Disable callout on iOS */
          -webkit-user-select: none !important; /* Disable text selection on iOS */
          touch-action: manipulation !important; /* Enable touch events */
        }

        /* 🚀 NEW: Mobile-specific touch styling */
        @media (max-width: 768px) {
          .jwlike .vjs-tech,
          .jwlike video {
            cursor: pointer !important;
            -webkit-tap-highlight-color: transparent !important; /* Remove tap highlight on mobile */
            -webkit-touch-callout: none !important;
            -webkit-user-select: none !important;
            touch-action: manipulation !important;
          }
        }

        /* 🚀 FIX: Text track display container for proper wrapping */
        .jwlike .vjs-text-track-display {
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          pointer-events: none !important;
          z-index: 15 !important;
          display: flex !important;
          align-items: flex-start !important; /* 🚀 FIX: Align to top for subtitle positioning */
          justify-content: center !important;
          padding: 50px 5% 0 5% !important; /* 🚀 FIX: Add top padding to match subtitle position */
        }

        /* 🚀 FIX: Individual cue styling for proper text flow */
        .jwlike .vjs-text-track-display .vjs-text-track-cue div {
          display: block !important;
          width: 100% !important;
          max-width: 100% !important;
          word-wrap: break-word !important;
          word-break: break-word !important;
          overflow-wrap: break-word !important;
          white-space: normal !important;
          hyphens: auto !important;
          text-align: center !important;
        }

        /* 🚀 FIX: Responsive subtitle sizing - 50% larger */
        @media (min-width: 1024px) {
          .jwlike .vjs-text-track-display .vjs-text-track-cue {
            position: absolute !important;
            bottom: 50px !important; /* 🚀 FIX: Force position at bottom on desktop */
            top: auto !important; /* 🚀 FIX: Remove top positioning */
            left: 0% !important; /* 🚀 FIX: Start from left edge (increased from 5%) */
            right: 0% !important; /* 🚀 FIX: End at right edge (increased from 5%) */
            transform: none !important; /* 🚀 FIX: Remove transform */
            width: auto !important; /* 🚀 FIX: Auto width within constraints */
            max-width: none !important; /* 🚀 FIX: Remove max-width constraint */
            font-size: 2.4em !important; /* 50% larger on desktop (1.6em * 1.5) */
            padding: 1.5em !important; /* 50% more padding (1em * 1.5) */
            margin: 0 !important; /* 🚀 FIX: Remove margin for perfect centering */
            word-wrap: break-word !important; /* 🚀 FIX: Break long words */
            word-break: break-word !important; /* 🚀 FIX: Force break long words */
            overflow-wrap: break-word !important; /* 🚀 FIX: Modern word wrapping */
            hyphens: auto !important; /* 🚀 FIX: Add hyphens for better breaking */
          }
        }

        @media (min-width: 1280px) {
          .jwlike .vjs-text-track-display .vjs-text-track-cue {
            position: absolute !important;
            bottom: 50px !important; /* 🚀 FIX: Force position at bottom on large screens */
            top: auto !important; /* 🚀 FIX: Remove top positioning */
            left: 0% !important; /* 🚀 FIX: Start from left edge (increased from 5%) */
            right: 0% !important; /* 🚀 FIX: End at right edge (increased from 5%) */
            transform: none !important; /* 🚀 FIX: Remove transform */
            width: auto !important; /* 🚀 FIX: Auto width within constraints */
            max-width: none !important; /* 🚀 FIX: Remove max-width constraint */
            font-size: 2.7em !important; /* 50% larger on large screens (1.8em * 1.5) */
            padding: 1.8em !important; /* 50% more padding (1.2em * 1.5) */
            margin: 0 !important; /* 🚀 FIX: Remove margin for perfect centering */
            word-wrap: break-word !important; /* 🚀 FIX: Break long words */
            word-break: break-word !important; /* 🚀 FIX: Force break long words */
            overflow-wrap: break-word !important; /* 🚀 FIX: Modern word wrapping */
            hyphens: auto !important; /* 🚀 FIX: Add hyphens for better breaking */
          }
        }

        /* 🚀 FIX: Tablet-specific sizing - 50% larger */
        @media (min-width: 769px) and (max-width: 1023px) {
          .jwlike .vjs-text-track-display .vjs-text-track-cue {
            position: absolute !important;
            bottom: 50px !important; /* 🚀 FIX: Force position at bottom on tablet */
            top: auto !important; /* 🚀 FIX: Remove top positioning */
            left: 0% !important; /* 🚀 FIX: Start from left edge (increased from 5%) */
            right: 0% !important; /* 🚀 FIX: End at right edge (increased from 5%) */
            transform: none !important; /* 🚀 FIX: Remove transform */
            width: auto !important; /* 🚀 FIX: Auto width within constraints */
            max-width: none !important; /* 🚀 FIX: Remove max-width constraint */
            font-size: 1.95em !important; /* 50% larger (1.3em * 1.5) */
            padding: 1.05em !important; /* 50% more padding (0.7em * 1.5) */
            margin: 0 !important; /* 🚀 FIX: Remove margin for perfect centering */
            word-wrap: break-word !important; /* 🚀 FIX: Break long words */
            word-break: break-word !important; /* 🚀 FIX: Force break long words */
            overflow-wrap: break-word !important; /* 🚀 FIX: Modern word wrapping */
            hyphens: auto !important; /* 🚀 FIX: Add hyphens for better breaking */
          }
        }

        /* 🚀 FIX: Small mobile devices - 50% larger */
        @media (max-width: 480px) {
          .jwlike .vjs-text-track-display .vjs-text-track-cue {
            position: absolute !important;
            bottom: 63px !important; /* 🚀 FIX: Move up 3px more from 60px to 63px on small phones */
            top: auto !important; /* 🚀 FIX: Remove top positioning */
            left: 50% !important; /* 🚀 FIX: Center horizontally */
            right: auto !important; /* 🚀 FIX: Remove right positioning */
            transform: translateX(-50%) !important; /* 🚀 FIX: Perfect center alignment */
            width: 90% !important; /* 🚀 FIX: Increase width by 20% on small phones (70% + 20% = 90%) */
            max-width: 90% !important; /* 🚀 FIX: Set max-width to 90% */
            font-size: 1.65em !important; /* 50% larger on small phones (1.1em * 1.5) */
            padding: 0.6em !important; /* 50% more padding (0.4em * 1.5) */
            margin: 0 !important; /* 🚀 FIX: Remove margin for perfect centering */
            word-wrap: break-word !important; /* 🚀 FIX: Break long words */
            word-break: break-word !important; /* 🚀 FIX: Force break long words */
            overflow-wrap: break-word !important; /* 🚀 FIX: Modern word wrapping */
            hyphens: auto !important; /* 🚀 FIX: Add hyphens for better breaking */
          }
        }
      `}</style>
      
      {/* 🚀 DEBUG: Log video element info */}
      {(() => {
        console.log('🔍 VideoJSPlayer - Rendering video element:', {
          videoUrl,
          vttUrl,
          translatedVttUrl,
          isLoading,
          hasError
        });
        return null;
      })()}
      <video
        ref={videoRef}
        className="video-js vjs-default-skin vjs-big-play-centered"
        controls
        preload="metadata"
        crossOrigin="anonymous"
        playsInline={true} // 🚀 FIX: Mobile Safari inline playback
        webkit-playsinline="true" // 🚀 FIX: Legacy iOS support
        style={{ 
          width: '100%', 
          height: '100%', 
          backgroundColor: '#000',
          objectFit: 'contain' // 🚀 FIX: Maintain aspect ratio
        }}
      />
      
      {isLoading && (
        <div 
          className="absolute inset-0 flex items-center justify-center bg-gray-100"
          style={{ pointerEvents: 'none' }} // ✅ không block hover/click
        >
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600 font-medium">Loading video...</p>
          </div>
        </div>
      )}
      
      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-red-50">
          <div className="text-center">
            <div className="text-red-600 text-4xl mb-4">⚠️</div>
            <p className="text-red-600 font-medium">Failed to load video</p>
            <p className="text-red-500 text-sm">Please try again or contact support</p>
          </div>
        </div>
      )}
    </div>
  );
}
